import {
  ColDef,
  ICellRendererParams,
  KeyCreatorParams,
  SetFilterValuesFuncParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-enterprise';
import { ActionButtonsCellRendererComponent, getEffectiveApprovedSince, RouterLinkCellRendererComponent } from '@workspace-app/shared';
import { CertifiableSystem, ProductionSite } from '@workspace-app/odatabackend/QualicoatBackendModel';
import { SystemAttributesCellRendererComponent } from './system-attributes-cell-renderer.component';

interface SetOption {
  label: string;
  value: string;
}

// Approval state badge styling
function getApprovalStateBadgeClass(state: string | null | undefined): string {
  switch (state) {
    case 'Final':
      return 'badge-success';
    case 'Interim':
      return 'badge-info';
    case 'Provisional':
      return 'badge-warning';
    case 'Granting':
      return 'badge-secondary';
    case 'Cancelled':
      return 'badge-danger';
    default:
      return 'badge-secondary';
  }
}

export function getApprovedSystemsColumns(
  caller?: { openTimeline: (system: CertifiableSystem) => void },
  options?: { glNamesFetcher?: (params: SetFilterValuesFuncParams) => void }
): ColDef[] {
  const columns: ColDef[] = [];

  columns.push({
    headerName: 'Actions',
    cellRenderer: ActionButtonsCellRendererComponent,
    cellRendererParams: {
      context: {
        timelineTestId: 'timeline-approved-system-button',
        timelineFn: caller ? (system: CertifiableSystem) => caller.openTimeline(system) : undefined,
      },
    },
    cellClass: 'action-buttons-cell',
    pinned: 'left',
    lockPosition: 'left',
    suppressMovable: true,
    minWidth: 50,
    width: 50,
    maxWidth: 50,
  });

  columns.push(
    {
      field: 'completeApprovalNumber',
      headerName: 'Approval number',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        filterOptions: ['equals', 'contains'],
        buttons: ['reset'],
      },
      floatingFilter: false,
      valueFormatter: (params: ValueFormatterParams<CertifiableSystem>) => {
        // Class-3 coating systems in Granting have no Number yet — render a "pending" placeholder
        // instead of the bare prefix so reviewers notice the system isn't fully granted.
        const data = params.data;
        if (data && data.number == null) {
          return `${data.prefix} (pending)`;
        }
        return params.value ?? '';
      },
    },
    {
      field: 'pCode',
      headerName: 'P-Code',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        filterOptions: ['contains', 'equals'],
        buttons: ['reset'],
      },
      floatingFilter: false,
    },
    {
      field: 'system',
      headerName: 'System',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        filterOptions: ['contains'],
        buttons: ['reset'],
      },
      floatingFilter: false,
    },
    {
      colId: 'supplierOrManufacturer',
      headerName: 'Manufacturer',
      valueGetter: (params: ValueGetterParams<CertifiableSystem>) => params.data?.supplierOrManufacturerOrganisation?.name ?? '',
      sortable: true,
      sort: 'asc',
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        filterOptions: ['contains'],
        buttons: ['reset'],
      },
      floatingFilter: false,
    },
    {
      field: 'systemType',
      headerName: 'System Type',
      sortable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        defaultToNothingSelected: true,
        values: (params: SetFilterValuesFuncParams<SetOption, SetOption>) => {
          return params.success([
            {
              label: 'Chemical Pretreatment',
              value: 'Chemical Pretreatment System',
            },
            {
              label: 'Chemical Pretreatment (new-tech)',
              value: 'Chemical Pretreatment System (for new technology)',
            },
            {
              label: 'Chemical Pretreatment (pre-ox)',
              value: 'Chemical Pretreatment System (for pre anodised material)',
            },
            {
              label: 'Coating System',
              value: 'Coating System',
            },
            {
              label: 'Sublimation Coating System',
              value: 'Organic Coating System for Sublimation',
            },
          ]);
        },
        keyCreator: (params: KeyCreatorParams) => params.value.value,
        valueFormatter: (params: ValueFormatterParams) => params.value.label,
        excelMode: 'windows',
        buttons: ['reset'],
      },
      floatingFilter: false,
    },
    {
      field: 'attributes',
      headerName: 'Attributes',
      sortable: false,
      minWidth: 100,
      maxWidth: 180,
      valueGetter: (params: ValueGetterParams) => {
        const data = params.data;
        const rinseProcess = data?.rinseProcess;
        const startDate = data?.outdoorExposureTestStartDate;
        let exposureYears: number | null = null;
        if (startDate) {
          const diffMs = Date.now() - new Date(startDate).getTime();
          exposureYears = Math.floor(diffMs / (365.25 * 24 * 60 * 60 * 1000));
        }

        // Powder system attributes (P- and DP- systems)
        const isPowderSystem = data?.prefix?.startsWith('P') || data?.prefix?.startsWith('DP');
        let bannedFamiliesCount = 0;
        let bannedColorsCount = 0;
        let suspendedColorsCount = 0;
        let provisionalColorsCount = 0;

        if (isPowderSystem) {
          bannedFamiliesCount = data?.bannedFamilies?.length || 0;
          const colors = data?.coatingSystemColors || [];
          bannedColorsCount = colors.filter((c: { colorApprovalStatus?: string }) =>
            c.colorApprovalStatus === 'Banned'
          ).length;
          suspendedColorsCount = colors.filter((c: { colorApprovalStatus?: string }) =>
            c.colorApprovalStatus === 'Suspended'
          ).length;
          provisionalColorsCount = colors.filter((c: { colorApprovalStatus?: string }) =>
            c.colorApprovalStatus === 'Provisional'
          ).length;
        }

        return {
          rinseProcess,
          exposureYears,
          isPowderSystem,
          bannedFamiliesCount,
          bannedColorsCount,
          suspendedColorsCount,
          provisionalColorsCount,
        };
      },
      cellRenderer: SystemAttributesCellRendererComponent,
      filter: false,
      floatingFilter: false,
    },
    {
      headerName: 'Production Site',
      sortable: true,
      cellRenderer: RouterLinkCellRendererComponent,
      cellRendererParams: (params: ICellRendererParams) => {
        if (!params.data.productionSite) return '';
        const productionSite = params.data.productionSite as ProductionSite;
        return {
          text: productionSite?.name,
        };
      },
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        filterOptions: ['contains'],
        buttons: ['reset'],
      },
      floatingFilter: false,
      hide: true,
    },
    {
      field: 'certificateExpiryDate',
      headerName: 'Certificate Expires',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        buttons: ['reset'],
        filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
      },
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        // Use UTC to avoid timezone shifting the date
        return new Date(params.value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        });
      },
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return '';
        const expiryDate = new Date(params.value);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        let badgeClass = '';
        if (daysUntilExpiry < 0) {
          badgeClass = 'text-danger';
        } else if (daysUntilExpiry <= 90) {
          badgeClass = 'text-warning';
        }

        // Use UTC to avoid timezone shifting the date
        const formattedDate = expiryDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        });
        return `<span class="${badgeClass}">${formattedDate}</span>`;
      },
    },
    {
      field: 'approvalState',
      headerName: 'State',
      sortable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        defaultToNothingSelected: true,
        values: (params: SetFilterValuesFuncParams<SetOption, SetOption>) => {
          return params.success([
            { label: 'Granting', value: 'Granting' },
            { label: 'Provisional', value: 'Provisional' },
            { label: 'Interim', value: 'Interim' },
            { label: 'Final', value: 'Final' },
            { label: 'Cancelled', value: 'Cancelled' },
          ]);
        },
        keyCreator: (params: KeyCreatorParams) => params.value.value,
        valueFormatter: (params: ValueFormatterParams) => params.value.label,
        excelMode: 'windows',
        buttons: ['reset'],
      },
      cellRenderer: (params: ICellRendererParams) => {
        const state = params.value || 'Granting';
        const badgeClass = getApprovalStateBadgeClass(state);
        return `<span class="badge ${badgeClass}">${state}</span>`;
      },
      floatingFilter: false,
      width: 120,
    },
    {
      field: 'approvedSince',
      headerName: 'Approved Since',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        buttons: ['reset'],
        filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
      },
      sortable: true,
      valueGetter: (params) => getEffectiveApprovedSince(params.data as CertifiableSystem | undefined),
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        });
      },
      hide: true,
    },
    {
      field: 'dateOfCancellation',
      headerName: 'Cancelled Since',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        buttons: ['reset'],
        filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
      },
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        });
      },
      hide: true, // Hidden by default, can be shown via column panel
    },
    {
      field: 'productionSite.customer.assignedGl.name',
      headerName: 'Assigned GL',
      sortable: true,
      initialHide: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        defaultToNothingSelected: true,
        values: options?.glNamesFetcher
          ? (params: SetFilterValuesFuncParams) => options.glNamesFetcher!(params)
          : [],
        buttons: ['reset'],
      },
      floatingFilter: false,
      minWidth: 150,
      width: 200,
      maxWidth: 250,
    }
  );

  return columns;
}
